import { Model, Factory, Response } from 'miragejs'
import type { ModelInstance } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randCompanyName, randNumber, randBoolean } from '@ngneat/falso'
import { filterable, createErrorResponse } from '../../utils.js'
import type { MirageServer, MockServer } from '../../server'
import type { SchemaAttributes } from '../../../types'

// Extend schema type with internal tracking fields (not returned in API response)
interface ItemSubgroupData extends SchemaAttributes<'ItemSubgroup'> {
  _parentId: string | null
  _accountNumberId: number | null
}

export const model = Model as ModelDefinition<ItemSubgroupData>

// Item type UPPERCASE codes
const ITEM_TYPE_CODES = [
  'PROCEDURE',
  'MEDICINE',
  'SUPPLY',
  'FOOD',
  'LABORATORY_ANALYSIS',
  'LABORATORY_ANALYSIS_PANEL',
]

export const factory = Factory.extend({
  title() {
    return `${randCompanyName()} Subgroup`
  },
  item_type(i: number) {
    return ITEM_TYPE_CODES[i % ITEM_TYPE_CODES.length]
  },
  performed_by_required() {
    return randBoolean()
  },
  separate_table_in_pdf() {
    return randBoolean()
  },
  item_count() {
    return randNumber({ min: 0, max: 100 })
  },
  children_count() {
    return 0
  },
  hierarchy_level() {
    return 0
  },
  _parentId() {
    return null
  },
  _accountNumberId() {
    return null
  },
})

// Helper to sort subgroups hierarchically (parents followed by their children)
function sortHierarchically(
  models: ModelInstance<ItemSubgroupData>[],
): ModelInstance<ItemSubgroupData>[] {
  const result: ModelInstance<ItemSubgroupData>[] = []
  const childrenMap = new Map<
    string | null,
    ModelInstance<ItemSubgroupData>[]
  >()

  // Build children map
  for (const model of models) {
    if (!model.id) {
      continue
    }
    const parentId = model.attrs._parentId ?? null
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, [])
    }
    childrenMap.get(parentId)!.push(model)
  }

  // Recursively add items in tree order
  function addWithChildren(parentId: string | null) {
    const children = childrenMap.get(parentId) || []
    children.sort((a, b) => a.attrs.title.localeCompare(b.attrs.title))
    for (const child of children) {
      result.push(child)
      if (child.id) {
        addWithChildren(child.id)
      }
    }
  }

  addWithChildren(null)
  return result
}

// Helper to build parent attribute for response
function buildParentAttr(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  parentId: string | null,
): { id: number; title: string } | null {
  if (!parentId) {
    return null
  }
  const parent = schema.find('itemSubgroup', parentId)
  if (!parent) {
    return null
  }
  return { id: Number(parent.id), title: parent.attrs.title }
}

// Helper to build response object for a subgroup
function buildSubgroupResponse(
  subgroup: ModelInstance<ItemSubgroupData>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
) {
  return {
    id: subgroup.id,
    type: 'ItemSubgroup',
    attributes: {
      title: subgroup.attrs.title,
      item_type: subgroup.attrs.item_type,
      parent: buildParentAttr(schema, subgroup.attrs._parentId),
      account_number: subgroup.attrs._accountNumberId,
      performed_by_required: subgroup.attrs.performed_by_required,
      separate_table_in_pdf: subgroup.attrs.separate_table_in_pdf,
      item_count: subgroup.attrs.item_count,
      children_count: subgroup.attrs.children_count,
      hierarchy_level: subgroup.attrs.hierarchy_level,
    },
  }
}

export function routes(server: MockServer) {
  /**
   * GET /catalog/management/item-subgroups/v1
   */
  server.get(
    '/catalog/management/item-subgroups/v1',
    function (this: MirageServer, schema, request) {
      const allSubgroups = schema.all('itemSubgroup')

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filtered = filterable(allSubgroups as any, request, {
        title: (item, value) =>
          (item as unknown as ModelInstance<ItemSubgroupData>).attrs.title
            .toLowerCase()
            .includes(value.toLowerCase()),
        item_type: (item, value) =>
          (
            (item as unknown as ModelInstance<ItemSubgroupData>).attrs
              .item_type ?? ''
          ).toUpperCase() === value.toUpperCase(),
      })

      const hierarchicallySorted = sortHierarchically(
        filtered.models as unknown as ModelInstance<ItemSubgroupData>[],
      )

      const pageParam = request.queryParams['page[number]']
      const pageSizeParam = request.queryParams['page[size]']
      const page = parseInt(
        Array.isArray(pageParam) ? pageParam[0] : pageParam || '1',
        10,
      )
      const pageSize = parseInt(
        Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam || '30',
        10,
      )
      const start = (page - 1) * pageSize
      const end = start + pageSize
      const paginatedModels = hierarchicallySorted.slice(start, end)
      const totalCount = hierarchicallySorted.length
      const totalPages = Math.ceil(totalCount / pageSize)

      return {
        data: paginatedModels.map((s) => buildSubgroupResponse(s, schema)),
        meta: {
          pagination: {
            count: totalCount,
            pages: totalPages,
          },
        },
      }
    },
  )

  /**
   * GET /catalog/management/item-subgroups/v1/:id
   */
  server.get('/catalog/management/item-subgroups/v1/:id', (schema, request) => {
    const id = request.params.id
    const subgroup = schema.find(
      'itemSubgroup',
      id,
    ) as ModelInstance<ItemSubgroupData> | null

    if (!subgroup) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `ItemSubgroup with id ${id} not found` }],
      })
    }

    return {
      data: buildSubgroupResponse(subgroup, schema),
    }
  })

  /**
   * POST /catalog/management/item-subgroups/v1
   */
  server.post('/catalog/management/item-subgroups/v1', (schema, request) => {
    const body = JSON.parse(request.requestBody)

    if (!body?.data?.attributes?.title) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "title" in request body' }],
      })
    }

    const { id, attributes } = body.data

    const parentId = attributes.parent_id ? String(attributes.parent_id) : null
    let hierarchyLevel = 0
    if (parentId) {
      const parentSubgroup = schema.find('itemSubgroup', parentId)
      if (parentSubgroup) {
        hierarchyLevel = (parentSubgroup.attrs.hierarchy_level ?? 0) + 1
      }
    }

    const createdRecord = schema.create('itemSubgroup', {
      id: id || undefined,
      title: attributes.title,
      item_type: attributes.item_type || 'PROCEDURE',
      performed_by_required: attributes.performed_by_required ?? true,
      separate_table_in_pdf: attributes.separate_table_in_pdf ?? false,
      item_count: 0,
      children_count: 0,
      hierarchy_level: hierarchyLevel,
      _parentId: parentId,
      _accountNumberId: attributes.account_number ?? null,
    })

    return {
      data: buildSubgroupResponse(
        createdRecord as unknown as ModelInstance<ItemSubgroupData>,
        schema,
      ),
    }
  })

  /**
   * PATCH /catalog/management/item-subgroups/v1/:id
   */
  server.patch(
    '/catalog/management/item-subgroups/v1/:id',
    (schema, request) => {
      const id = request.params.id
      const body = JSON.parse(request.requestBody)
      const subgroup = schema.find(
        'itemSubgroup',
        id,
      ) as ModelInstance<ItemSubgroupData> | null

      if (!subgroup) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `ItemSubgroup with id ${id} not found` }],
        })
      }

      const { attributes } = body.data || {}

      // Handle parent_id update
      let newParentId = subgroup.attrs._parentId
      let newHierarchyLevel = subgroup.attrs.hierarchy_level

      if (attributes?.parent_id !== undefined) {
        newParentId =
          attributes.parent_id !== null ? String(attributes.parent_id) : null
        if (newParentId === null) {
          newHierarchyLevel = 0
        } else {
          const parentSubgroup = schema.find('itemSubgroup', newParentId)
          if (parentSubgroup) {
            newHierarchyLevel = (parentSubgroup.attrs.hierarchy_level ?? 0) + 1
          }
        }
      }

      subgroup.update({
        title: attributes?.title ?? subgroup.attrs.title,
        item_type: attributes?.item_type ?? subgroup.attrs.item_type,
        performed_by_required:
          attributes?.performed_by_required ??
          subgroup.attrs.performed_by_required,
        separate_table_in_pdf:
          attributes?.separate_table_in_pdf ??
          subgroup.attrs.separate_table_in_pdf,
        hierarchy_level: newHierarchyLevel,
        _parentId: newParentId,
        _accountNumberId:
          attributes?.account_number !== undefined
            ? attributes.account_number
            : subgroup.attrs._accountNumberId,
      })

      return {
        data: buildSubgroupResponse(subgroup, schema),
      }
    },
  )

  /**
   * DELETE /catalog/management/item-subgroups/v1/:id
   */
  server.delete(
    '/catalog/management/item-subgroups/v1/:id',
    (schema, request) => {
      const id = request.params.id
      const subgroup = schema.find(
        'itemSubgroup',
        id,
      ) as ModelInstance<ItemSubgroupData> | null

      if (!subgroup) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `ItemSubgroup with id ${id} not found` }],
        })
      }

      // Reparent children to the deleted node's parent before destroying
      const allSubgroups = schema.all('itemSubgroup')
      for (const child of allSubgroups.models) {
        if (child.attrs._parentId === id) {
          child.update({ _parentId: subgroup.attrs._parentId })
        }
      }

      subgroup.destroy()

      return new Response(204, {}, '')
    },
  )
}

export function seeds(server: MockServer) {
  const procedures = server.create('itemSubgroup', {
    title: 'General Procedures',
    item_type: 'PROCEDURE',
    performed_by_required: true,
    separate_table_in_pdf: false,
    item_count: 25,
    children_count: 2,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Surgical Procedures',
    item_type: 'PROCEDURE',
    performed_by_required: true,
    separate_table_in_pdf: false,
    item_count: 15,
    children_count: 0,
    hierarchy_level: 1,
    _parentId: procedures.id,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Diagnostic Procedures',
    item_type: 'PROCEDURE',
    performed_by_required: true,
    separate_table_in_pdf: false,
    item_count: 10,
    children_count: 0,
    hierarchy_level: 1,
    _parentId: procedures.id,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Prescription Medicines',
    item_type: 'MEDICINE',
    performed_by_required: false,
    separate_table_in_pdf: false,
    item_count: 150,
    children_count: 0,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Medical Supplies',
    item_type: 'SUPPLY',
    performed_by_required: false,
    separate_table_in_pdf: false,
    item_count: 75,
    children_count: 0,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Prescription Diets',
    item_type: 'FOOD',
    performed_by_required: false,
    separate_table_in_pdf: false,
    item_count: 30,
    children_count: 0,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Blood Tests',
    item_type: 'LABORATORY_ANALYSIS',
    performed_by_required: false,
    separate_table_in_pdf: true,
    item_count: 20,
    children_count: 0,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  server.create('itemSubgroup', {
    title: 'Complete Blood Count Panel',
    item_type: 'LABORATORY_ANALYSIS_PANEL',
    performed_by_required: false,
    separate_table_in_pdf: true,
    item_count: 5,
    children_count: 0,
    hierarchy_level: 0,
    _parentId: null,
    _accountNumberId: null,
  })

  // Add some random ones (root level)
  server.createList('itemSubgroup', 5)
}
